import type { JsmCustomField } from "@/hooks/use-itsm-metadata";
import type { CustomFieldMapping } from "@/lib/types/itsm-import";

export const JSM_FIELD_ID_PATTERN = /^customfield_\d+$/;

/** Custom field selection is offered for JSM, and for Jira credentials that
 *  are imported through the JSM provider. */
/** Listed by default in the picker: a requester fills it in on a request
 *  form, or agents see it at the top level of a request type's issue view. */
export function isDefaultViewField(field: JsmCustomField): boolean {
  return field.in_request_form || field.in_issue_view === true;
}

export function supportsCustomFieldMappings(provider: string | undefined) {
  return provider === "jsm" || provider === "jira";
}

export interface MappingError {
  kind: "missing" | "duplicate" | "unknown";
  message: string;
}

/** Row errors keyed by source_field_id. `harmonyFieldNames` is the target
 *  desk's field names, or undefined while they are unknown. */
export function getMappingErrors(
  mappings: readonly CustomFieldMapping[],
  harmonyFieldNames?: ReadonlySet<string>,
): Map<string, MappingError> {
  const errors = new Map<string, MappingError>();
  const claimedBy = new Map<string, string>();
  for (const m of mappings) {
    if (m.method !== "harmony_custom_field") continue;
    const target = m.harmony_field_name;
    if (!target) {
      errors.set(m.source_field_id, {
        kind: "missing",
        message: "Choose a Harmony field",
      });
      continue;
    }
    if (harmonyFieldNames && !harmonyFieldNames.has(target)) {
      errors.set(m.source_field_id, {
        kind: "unknown",
        message: `"${target}" is not a custom field on the target desk`,
      });
      continue;
    }
    const owner = claimedBy.get(target);
    if (owner === undefined) {
      claimedBy.set(target, m.source_field_name ?? m.source_field_id);
    } else {
      errors.set(m.source_field_id, {
        kind: "duplicate",
        message: `"${target}" is already mapped from ${owner}`,
      });
    }
  }
  return errors;
}
